import { App } from '../BMapsApp';
import { EventBroker } from '../eventbroker';
import { fepFormDefaults } from '../integrations/fep';
import { UserActions, UserCmd } from './UserCmd';
import { setInfoDisplayPanelOpen } from '../redux/projectState/access';

/**
 * @typedef UICmds
 * @type {object}
 * @property {openImport} OpenImport
 * @property {openProteinImport} OpenProteinImport
 * @property {stageMoleculeImport} StageMoleculeImport
 * @property {stageProteinImport} StageProteinImport
 * @property {openExport} OpenExport
 * @property {openDock} OpenDock
 * @property {openSketcher} OpenSketcher
 * @property {openFragmentManager} OpenFragmentManager
 * @property {showInspectorTab} ShowInspectorTab
 * @property {expandInfoDisplayPanel} ExpandInfoDisplayPanel
 * @property {collapseInfoDisplayPanel} CollapseInfoDisplayPanel
 * @property {showFragmentPane} ShowFragmentPane
 * @property {openMapSelector} OpenMapSelector
 * @property {openSelectBox} OpenSelectBox
 * @property {openSidePanel} OpenSidePanel
 * @property {closeSidePanel} CloseSidePanel
 * @property {requestMinimize} RequestMinimize
 * @property {openRenameCompound} OpenRenameCompound
 * @property {openScreeningPanel} OpenScreeningPanel
 * @property {openCompoundsPanel} OpenCompoundsPanel
 * @property {openScoringPanel} OpenScoringPanel
 * @property {openFreeEnergyPanel} OpenFreeEnergyPanel
 * @property {openJobsPanel} OpenJobsPanel
 */

/** @type {UICmds} */
export const UICmds = {
    OpenImport: new UserCmd('OpenImport', openImport),
    OpenProteinImport: new UserCmd('OpenProteinImport', openProteinImport),
    StageMoleculeImport: new UserCmd('StageMoleculeImport', stageMoleculeImport),
    StageProteinImport: new UserCmd('StageProteinImport', stageProteinImport),
    OpenExport: new UserCmd('OpenExport', openExport),
    OpenDock: new UserCmd('OpenDock', openDock),
    OpenSketcher: new UserCmd('OpenSketcher', openSketcher),
    OpenFragmentManager: new UserCmd('OpenFragmentManager', openFragmentManager),
    ShowInspectorTab: new UserCmd('ShowInspectorTab', showInspectorTab),
    // Layout only: not undoable, and a pending redo survives them.
    ExpandInfoDisplayPanel: new UserCmd('ExpandInfoDisplayPanel', expandInfoDisplayPanel, { noEffect: true }),
    CollapseInfoDisplayPanel: new UserCmd('CollapseInfoDisplayPanel', collapseInfoDisplayPanel, { noEffect: true }),
    ShowFragmentPane: new UserCmd('showFragmentPane', showFragmentPane),
    OpenMapSelector: new UserCmd('OpenMapSelector', openMapSelector),
    OpenSelectBox: new UserCmd('OpenSelectBox', openSelectBox),
    OpenSidePanel: new UserCmd('OpenSidePanel', openSidePanel),
    CloseSidePanel: new UserCmd('CloseSidePanel', closeSidePanel),
    RequestMinimize: new UserCmd('RequestMinimize', requestMinimize),
    OpenRenameCompound: new UserCmd('OpenRenameCompound', openRenameCompound),
    OpenScreeningPanel: new UserCmd('OpenScreeningPanel', openScreeningPanel),
    OpenCompoundsPanel: new UserCmd('OpenCompoundsPanel', openCompoundsPanel),
    OpenScoringPanel: new UserCmd('OpenScoringPanel', openScoringPanel),
    OpenFreeEnergyPanel: new UserCmd('OpenFreeEnergyPanel', openFreeEnergyPanel),
    OpenJobsPanel: new UserCmd('OpenJobsPanel', openJobsPanel),
};

function openImport(tabId, args) {
    EventBroker.publish('openImportTab', { tabId, args });
}

function openProteinImport(tabId, args) {
    EventBroker.publish('openProteinImportTab', { tabId, args });
}

function stageMoleculeImport(stageArgs={}) {
    // undefined = Use default import tab
    openImport(undefined, stageArgs);
}

function stageProteinImport(stageArgs={}) {
    openProteinImport('import_protein_tab', stageArgs);
}

function openExport({ compounds: compoundsIn=[], atoms: atomsIn=[] }={}, args) {
    let compounds = compoundsIn;
    let atoms = atomsIn;
    // Use defaults if nothing specified
    if (compounds.length === 0 && atoms.length === 0) {
        compounds = App.Workspace.getSelectedOrActiveCompounds();
        atoms = App.Workspace.getSelectedAtoms();
        if (atoms.length === 1) {
            // Selected atoms currently take priority over compounds.
            // In the case of a single selected atom, don't send it,
            // to avoid stray forgotten selections interfering with exporting
            // compounds.
            // Better: give a selector on the export screen to choose between
            // selected atoms and selected compounds.
            atoms = [];
        }
    }
    EventBroker.publish('askExport', [{ compounds, atoms }, args]);
}

function openDock(compoundsIn=[]) {
    let compounds = [].concat(compoundsIn); // guarantee array
    if (compounds.length === 0) {
        compounds = compounds.concat(App.Workspace.getSelectedOrActiveCompounds());
    }
    if (compounds.length > 0) {
        EventBroker.publish('askDock', { compounds });
    }
}

function openSketcher(compound) {
    EventBroker.publish('askSketcher', { compound });
}

function openFragmentManager(initialMode) {
    EventBroker.publish('openFragmentManager', { initialMode });
}

/**
 * Select a tab of the Inspector. By default only the tab: a hidden Inspector stays hidden
 * (Minimize and Calculate Energies rely on that). Callers that are an explicit request to see the
 * tab -- the command panel / voice and Gibbs -- pass `ensureVisible` to show it as well.
 * @param {string} tab
 * @param {{ ensureVisible?: boolean }} [options]
 */
function showInspectorTab(tab, { ensureVisible=false } = {}) {
    if (ensureVisible) setInfoDisplayPanelOpen('inspector', true);
    App.Workspace.displayStateController.setInspectorTab(tab);
}

/**
 * Show (expand) an InfoDisplay panel, the Selector or Inspector. Sets an explicit value rather
 * than toggling, so repeated calls are harmless.
 * @param {'selector'|'inspector'} panel
 */
function expandInfoDisplayPanel(panel) {
    setInfoDisplayPanelOpen(panel, true);
}

/**
 * Hide (collapse) an InfoDisplay panel. The panel is hidden, not unmounted (unlike
 * CloseSidePanel), so its local state survives.
 * @param {'selector'|'inspector'} panel
 */
function collapseInfoDisplayPanel(panel) {
    setInfoDisplayPanelOpen(panel, false);
}

function showFragmentPane(which) {
    EventBroker.publish('showFragmentPane', { type: which });
}

/**
 * @param {'show' | 'hide'} cmd
 *
 * @param {{
 *      selectivity?: 'individual' | 'selectivity' | 'viewonly' | 'multiProtein'
 * }} options
 */

function openMapSelector(cmd, options={}) {
    openProteinImport('protein_search_tab', { cmd, options });
}

function openSelectBox(arg) {
    EventBroker.publish('openSelectBox', arg);
}

function openSidePanel(positionId, args) {
    EventBroker.publish(`set${positionId}Panel`, args);
}

function closeSidePanel(positionId) {
    EventBroker.publish(`set${positionId}Panel`);
}

function requestMinimize() {
    UserActions.ShowInspectorTab('energies');
    UserActions.EnergyMinimize(App.Workspace.getSelectedOrActiveCompounds());
}

function openRenameCompound(compound) {
    EventBroker.publish('askRenameCompound', { compound });
}

/**
 * Open the Screening panel, optionally with the chemist's chosen library already selected.
 *
 * Same dual form as `openFreeEnergyPanel` below, and for the same reason: a bare tab id from the
 * Jobs Management links ("View Screening Jobs"), or `{tabId, library, screenType, jobName}` from
 * the Gibbs handoff.
 *
 * **Why it was one argument.** Gibbs asks which of the 536-target panels to screen against --
 * a selectivity question with a real answer -- and the chemist's answer then vanished: the panel
 * opened on its DEFAULT library. The receiving end was never the problem. `NewScreen` already
 * reads `initData.library` and preselects it, and `openScreeningSidePanel(tabId, initData)`
 * already takes the data. The only break was in between: this function published a bare tab id
 * and the subscriber dropped everything else. One link in an otherwise finished chain.
 *
 * @param {string|{tabId?: string, library?: string, screenType?: string, jobName?: string}}
 *   tabIdOrParams
 */
function openScreeningPanel(tabIdOrParams) {
    const fromParams = tabIdOrParams !== null && typeof tabIdOrParams === 'object';
    if (!fromParams) {
        EventBroker.publish('askOpenScreeningPanel', tabIdOrParams);
        return;
    }
    const {
        tabId, library, screenType, jobName,
    } = tabIdOrParams;
    const initData = {};
    // Only what was actually supplied: an explicit `undefined` would override NewScreen's own
    // fallback and is not the same as "not chosen".
    if (library) initData.library = library;
    if (screenType) initData.screenType = screenType;
    if (jobName) initData.jobName = jobName;
    EventBroker.publish('askOpenScreeningPanel', { tabId, initData });
}

/**
 * The four job lists, by the word a chemist uses for them.
 *
 * BMaps has no single "jobs panel": Jobs Management (ui/ServerInfo.jsx) is four links into four
 * different panes, and those four openers are what this delegates to. Asked to "open the jobs
 * panel", the planner had no action naming any of them -- `open-side-panel` declares
 * `['docking','help','workflow','results']`, so the model picked the nearest, `workflow`, and
 * chat reported success for the Workflow Checker. A model cannot choose what it was never
 * shown; the keys here are that missing vocabulary.
 *
 * Keyed by category rather than split into four actions so that "open the jobs panel" has ONE
 * match and an unspecified category is a missing required parameter -- which the clarification
 * cascade already knows how to ask about. Four actions would have reproduced the ambiguity that
 * caused the wrong panel in the first place.
 */
const JOBS_VIEWS = {
    folding: {
        label: 'protein folding',
        open: () => openProteinImport('jobs_structures_tab'),
    },
    compound: {
        label: 'compound generation',
        open: () => openImport('jobs_compounds_tab'),
    },
    screening: {
        label: 'screening',
        open: () => openScreeningPanel('screen_jobs_tab'),
    },
    // Not gated on Loader.AllowLabFeatures, although the Jobs Management LINK is: the
    // `open-free-energy-panel` action it delegates to is itself ungated, so gating only this
    // wrapper would make the same pane reachable by one action and refused by another.
    'free-energy': {
        label: 'free energy',
        open: () => openFreeEnergyPanel('fep_jobs_tab'),
    },
};

/**
 * Open one of the four job lists.
 *
 * Returns a `summary` either way, which is what the chemist is told -- the dispatcher narrates
 * a callee's own sentence in preference to anything else (cmds/gibbsDispatch.planNarration).
 * An unknown category REFUSES and names the four that exist, rather than opening whichever one
 * happens to be first: opening the wrong panel and reporting success is the defect this
 * function was added to remove, and silently defaulting would reintroduce it one layer down.
 *
 * @param {'folding'|'compound'|'screening'|'free-energy'} category
 * @returns {{summary: string, error?: string}}
 */
function openJobsPanel(category) {
    const view = JOBS_VIEWS[category];
    if (!view) {
        return {
            error: 'unknown_jobs_view',
            summary: `I do not have a "${category}" job list. The job lists are: `
                + `${Object.keys(JOBS_VIEWS).join(', ')}.`,
        };
    }
    view.open();
    return { summary: `Opened the ${view.label} jobs list.` };
}

/**
 * Open the Free Energy (FEP) panel, optionally with the campaign's chemistry
 * already chosen.
 *
 * The Gibbs pre-fill handoff, and the same shape as `openScreeningPanel` above
 * for the same reason: a relative-vs-absolute calculation, an engine, a
 * compute target and a sampling profile are chemistry decisions with real cost
 * and real error consequences (fast sampling is ~5x cheaper and noticeably
 * less accurate), so Gibbs collects them in chat and hands the chemist a
 * filled-in form to review rather than submitting a multi-hour campaign blind.
 *
 * Two call shapes, both real:
 *   - a bare tab id, from CmdInterpreter's command table;
 *   - the whole params object, from the ActionDef dispatch path, which passes
 *     `userAction(...inputArgs, paramObj)`; this ActionDef declares no inputs,
 *     so the params object arrives as the FIRST argument.
 *
 * Only the fields the panel actually reads are forwarded, and only when set,
 * so the panel's defaults never come to depend on whatever the registry
 * happens to declare next. `fepFormDefaults` then validates each one against
 * the real option list, so a bad guess lands on the default.
 */
function openFreeEnergyPanel(tabIdOrParams, maybeInitData) {
    const fromParams = tabIdOrParams !== null && typeof tabIdOrParams === 'object';
    const tabId = fromParams ? tabIdOrParams.tabId : tabIdOrParams;
    let initData = maybeInitData;
    if (fromParams) {
        const {
            calculation, engine, target, mode, runOptimization, networkPolicy, jobName,
        } = tabIdOrParams;
        const picked = {};
        if (calculation) picked.calculation = calculation;
        if (engine) picked.engine = engine;
        if (target) picked.target = target;
        if (mode) picked.mode = mode;
        if (runOptimization) picked.runOptimization = runOptimization;
        if (networkPolicy) picked.networkPolicy = networkPolicy;
        if (jobName) picked.jobName = jobName;
        initData = Object.keys(picked).length > 0 ? picked : tabIdOrParams.initData;
        // Stamp each explicit open so the form can tell "Gibbs asked again, with
        // different choices" from "initData changed incidentally". The panel
        // seeds its controls once on mount by design -- a later initData change
        // must not yank a knob out from under someone mid-edit -- but that made
        // a SECOND handoff silently keep the first one's values while the chat
        // reported the new ones. Re-seeding only on a changed nonce keeps the
        // original intent and fixes the misleading case.
        if (initData) initData = { ...initData, prefillNonce: Date.now() };
    }
    EventBroker.publish('askOpenFreeEnergyPanel', { tabId, initData });
    // Report what the form will actually seed from, not what was asked for, so
    // the caller can narrate the RESULT. `fepFormDefaults` validates each field
    // against the real option list, so an unrecognised value shows here as the
    // default it fell back to rather than as the thing the user said.
    return { applied: fepFormDefaults(initData || {}, 0), tabId };
}

function openCompoundsPanel() {
    EventBroker.publish('askOpenCompoundsPanel');
}

function openScoringPanel() {
    EventBroker.publish('askOpenScoringPanel');
}
